import pageServices from '@/services/page';
import { Decrypt } from '@/helpers/utils';
import eventBus from 'vue-option-events';
import * as HANDLERS from '@/helpers/types/handlers';
import {
  setActionsCondition,
  getListIndex,
  initLookups,
  initLookupsList,
  setLookupListScheme,
  conditionEngine,
  lookupEngine,
  showMessage,
  checkParams,
  loadMethod,
  downloadECM,
  uploadEcm,
  generatePdfReport,
  generateExcelReport,
  generateTxtReport,
  addFilterParams,
  redirect,
  getSelectedItems,
  afterEvents,
  sleep
} from '@/helpers/page';

export default {
  setMain: ({ commit }, page) => commit('setMain', page),

  executePageEvents: ({ state, commit, dispatch }) => pageEvents(state, commit, dispatch, { page: 'dev', mode: 'list' }),

  updateSelectedItems: ({ commit }, params) => commit('updateSelectedItems', params),

  clear: ({ commit }) => commit('clear'),

  clearFilter: ({ commit }) => commit('clearFilter'),

  setTabScheme: async ({ commit }, { page, mode, tab }) => await commit('setTabScheme', { page, mode, tab }),

  setStepScheme: async ({ commit }, { page, mode, tab }) => await commit('setStepScheme', { page, mode, tab }),

  lookup: async ({ dispatch }, params) => (params.info.mode === 'list' ? dispatch('lookupList', params) : dispatch('lookupForm', params)),

  setLookupListScheme: async ({ commit, state }, page) => await setLookupListScheme(commit, state, page),

  loadLookupsList: async ({ commit, state }, params) => initLookupsList(commit, state, params),

  initConditionsList: async ({ commit }, params) => commit('initConditionsList', params),

  initInlineConditionsList: async ({ commit }, params) => commit('initInlineConditionsList', params),

  setActionsCondition: async ({ commit }, page) => await setActionsCondition(commit, page),

  hideGroup: async ({ commit }, params) => commit('hideGroup', params),

  showGroups: async ({ commit }, params) => commit('showGroups', params),

  load: async ({ commit, state, dispatch, rootState }, params) => {
    params = await checkParams(params, commit);
    const execute = await loadMethod(state, params);
    await execute(state, commit, dispatch, params, rootState);
  },

  loadTab: async ({ commit, state }, { mode, page, tab }) => {
    page === 'dev' && (await sleep(50));

    let procedure = state.schemes[page][mode].tabs.content[tab].procedure;
    procedure.model = state.models[page]['master'];
    procedure.invokeName = 'redirect';

    const { data } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

    if (data.invalid) {
      showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });

      return;
    }

    await commit('setModel', { page, data: data.values[0], mode });
  },

  loadLookups: async ({ commit, state }, params) => {
    if (params.page === 'dev') return; // Mokcar dados para initlookup(b_portal_dev)

    return initLookups(commit, state, params);
  },

  conditions: async ({ commit }, params) => {
    if (params.mode === 'list') {
      commit('setInlineConditionsList', params);
    } else {
      commit('setConditions', params);
    }
  },

  getEntry: async ({ commit, state, dispatch }, { page, entry, mode, changeMode, navigation, resetTab }) => {
    if (page === 'dev') {
      showMessage(commit, {
        message: 'Você está em modo DEV, não é permitido executar ação de redirect!',
        color: 'warning'
      });
      return;
    }

    if (!entry) {
      if (navigation) {
        const index = await getListIndex(page, state.models[page]['list'], navigation, state);
        const item = state.models[page]['list'][index];
        if (item && index !== -1) entry = item;
        else return;
      } else entry = state.entries[page];
    }

    const masterContent = 'master' in state.schemes[page][mode];
    const stepContent = 'steps' in state.schemes[page][mode];

    await commit('setEntry', { page, entry });

    if (stepContent) {
      await commit('changePageMode', { page, mode });

      return;
    }

    let { procedure } = state.schemes[page][mode];
    procedure.model = entry;
    procedure.invokeName = 'redirect';

    const { data } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

    if (data.invalid) {
      showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });

      return;
    }

    await commit('setModel', { page, data: data.values[0], mode, masterContent, changeMode });

    if (changeMode) await commit('changePageMode', { page, mode });

    if (masterContent && 'fields' in state.schemes[page][mode].master) {
      await initLookups(commit, state, { page, mode: 'master' });
    } else {
      await initLookups(commit, state, { page, mode });
      dispatch('conditions', { page, mode });
    }

    if (navigation || resetTab) {
      await sleep(200);
      eventBus.$emit(HANDLERS.RESET_TAB, page);
    }
  },

  loadStep: async ({ commit, state }, { page, mode, step }) => {
    let procedure = state.schemes[page][mode].steps.content[step].procedure;

    procedure.model = state.entries[page];
    procedure.invokeName = 'redirect';

    const { data } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

    if (data.invalid) {
      showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });

      return;
    }

    await commit('setModel', { page, data: data.values[0], mode });

    commit('setStepScheme', { page, mode, step });
  },

  lookupForm: async ({ state, commit }, params) => {
    let { page, mode, tab, step } = params.info;
    let allFields = [];

    if (tab) {
      allFields = state.schemes[page][mode].tabs.content[tab].fields;
      state.schemes[page][mode].tabs.content[tab].groups.filter(group => group.fields.map(field => (allFields = [...allFields, field])));
    } else if (step) {
      allFields = state.schemes[page][mode].steps.content[step].fields;
      state.schemes[page][mode].steps.content[step].groups.filter(group => group.fields.map(field => (allFields = [...allFields, field])));
    } else {
      allFields = state.schemes[page][mode].fields;
      state.schemes[page][mode].groups.filter(group => group.fields.map(field => (allFields = [...allFields, field])));
    }

    for (let item of params.fill) {
      let field = allFields.find(p => p.model === item);

      let model = {
        name: item,
        model: params.model,
        fill: params.fill,
        filledBy: params.filledBy,
        InvokeName: field.fieldName,
        procedure: field.procedure.name,
        page,
        typeLookup: 'typeLookup' in field ? field.typeLookup : null
      };

      const { data } = await pageServices.lookups(model);

      params.model = data.model;

      if (field.typeLookup && field.typeLookup !== 'conditional') commit('setLookup', { data: data, info: params.info });

      commit('updateModel', { page, mode, model: data.model });

      field.hasConditions && commit('setConditions', params.info);
    }
  },

  lookupList: async ({ state, commit }, params) => {
    let { page, mode } = params.info;
    let param = state.schemes[page][mode].attributes.attributesTable.keyValue;
    let key = params.model[param];
    let allFields = state.schemes[page][mode].lookups[key];

    for (let item of params.fill) {
      let field = allFields.find(p => p.model === item);

      let model = {
        name: item,
        model: params.model,
        fill: params.fill,
        filledBy: params.filledBy,
        procedure: field.procedure.name,
        InvokeName: field.fieldName,
        page,
        typeLookup: 'typeLookup' in field ? field.typeLookup : null
      };

      const { data } = await pageServices.lookups(model);

      commit('setLookupList', { data, info: params.info });
      commit('updateModelList', { page, mode, model: data.model });
    }
  },

  checkEletronicSignature: async ({ commit, dispatch, state }, { page, mode, origin, action, master, child, name, model }) => {
    let procedure = {
      name,
      page,
      initialModel: { [action.before.procedure.model]: null },
      invokeName: 'Assinatura Eletrônica',
      model: model
    };

    var { data } = await pageServices.executeProcedureAction(addFilterParams(state, procedure));

    if (data.invalid) {
      showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });
      return;
    }

    if (data.dictionaryReturns['@id_retorno'] < 0) {
      showMessage(commit, {
        message: data.dictionaryReturns['@ds_retorno'],
        color: data.dictionaryReturns['@message_class'] || 'error'
      });

      return;
    } else if (data.dictionaryReturns['@id_retorno'] > 0) {
      showMessage(commit, {
        message: data.dictionaryReturns['@ds_retorno'],
        color: data.dictionaryReturns['@message_class'] || 'warning'
      });

      return;
    } else {
      eventBus.$emit(HANDLERS.CLOSE_DIALOG_ELECTRONIC_SIGNATURE);

      dispatch('executeAction', { page, mode, origin, action, master, child });
    }
  },

  pageEvents: async ({ state, commit, dispatch }, params) => {
    const { page, mode, redirectParams, target, setAutomatically, master } = params;
    const bgPage = state.schemes[page].background;

    if (page != 'footer') eventBus.$emit(HANDLERS.SHOW_BACKGROUND, bgPage);

    if (master) {
      await sleep(30);
      dispatch('getEntry', { page, mode: master || mode, entry: redirectParams, changeMode: true });
    } else {
      await lookupEngine(commit, state, dispatch, { page, mode });
      conditionEngine(commit, state, dispatch, { page, mode });
    }

    if (setAutomatically) {
      if (
        mode === 'list' && state.models[page][mode].length === 1 && state.schemes[page][mode].display === 'list'
          ? state.schemes[page][mode].targetList === 'page'
          : state.schemes[page][mode].targetCard === 'page'
      ) {
        let newMode =
          state.schemes[page][mode].display === 'list'
            ? state.schemes[page][mode].attributes.attributesTable.editMode
            : state.schemes[page][mode].attributes.attributesDataIterator.editMode;
        await sleep(30);
        dispatch('getEntry', { page, mode: newMode, entry: state.models[page][mode][0], changeMode: true });
      }
    }
    if (mode === 'list' && state.main == page) commit('clearFilter');
  },

  executeAction: async ({ commit, state, dispatch, rootState }, params) => {
    eventBus.$emit(HANDLERS.INIT_LOADING, params.action);

    let model = params.model ? params.model : state.models[params.page][params.master ? 'master' : params.mode];
    let models = Decrypt(sessionStorage.getItem('data'), 'i4pro');
    let initialModel = params.mode == 'list' && params.index ? models[params.page][params.mode][params.index] : models[params.page][params.mode];
    let actionType;
    let requestParams = {};
    let twoModels = false;
    let originalMode;
    let uploadResult;

    if (params.page === 'dev' && 'redirect' in params.action) {
      showMessage(commit, { message: 'Redirect não permitido! (DEV MODE ON)', color: 'warning' });
      eventBus.$emit(HANDLERS.FINISH_LOADING);
      return;
    }

    if (params.child && params.action.useParentModel) {
      twoModels = true;
      originalMode = params.mode;
      params.mode = 'list';
      initialModel = params.mode == 'list' && params.index ? models[params.page][params.mode][params.index] : models[params.page][params.mode];
    }

    try {
      if ('txt' in params.action) {
        generateTxtReport(params.action, model);
        return;
      }

      if ('report' in params.action) {
        generatePdfReport(params.action, model);
        return;
      }

      if ('excel' in params.action) {
        generateExcelReport(params.action, model);
        return;
      }

      if ('redirect' in params.action) {
        redirect(state, commit, params);
        return;
      }

      if ('ecm' in params.action) {
        if (params.action.ecm.download) {
          downloadECM(params.action, model);
          return;
        }
        uploadResult = await uploadEcm(state, params, rootState, originalMode, commit);
      }

      if (params.master) params.mode = 'master';

      if (params.mode === 'list') {
        const result = getSelectedItems(state, params.page, model, initialModel);
        model = result.model;
        initialModel = result.initialModel;
      }

      if ('procedure' in params.action) {
        requestParams = JSON.parse(JSON.stringify(params.action.procedure));
        actionType = 'procedure';
      }

      if ('component' in params.action) {
        requestParams = JSON.parse(JSON.stringify(params.action.component));
        actionType = 'component';
      }

      if (!actionType) {
        showMessage(commit, { message: 'Não foi definido uma Procedure ou Componente para esta ação.', color: 'error' });
        return;
      }

      requestParams.page = params.page;
      requestParams.initialModel = initialModel;
      requestParams.InvokeName = params.action.actionName;
      if (params.mode !== 'search') requestParams.Entry = state.entries[params.page];

      if (actionType !== 'procedure') {
        requestParams.className = requestParams.params.className;
        requestParams.methodName = requestParams.params.methodName;
        requestParams.nameDll = requestParams.params.nameDll;
        requestParams.namespace = requestParams.params.namespace;
      }

      if (twoModels) requestParams.auxiliaryModel = state.models[params.page][originalMode];

      requestParams.model = Object.assign(model);

      if (uploadResult) {
        if (params.mode === 'list') {
          for (const idx in requestParams.model) {
            requestParams.model[idx][uploadResult[idx].model] = uploadResult[idx].keyValue;
            requestParams.model[idx][`${uploadResult[idx].model}_id_documento`] = uploadResult[idx].value;
          }
        } else {
          for (let file of uploadResult) {
            requestParams.model[file.model] = file.keyValue;
            requestParams.model[`${file.model}_id_documento`] = file.value;
          }
        }
      }

      let files = {};
      if (!('ecm' in params.action)) {
        for (let [key, value] of Object.entries(requestParams.model)) {
          if (value instanceof File) {
            files[key] = value;
            requestParams.model[key] = null;
          }
        }
      }

      if (params.mode !== 'list' || 'index' in params) {
        var { data } =
          actionType === 'procedure'
            ? await pageServices.executeProcedureAction(addFilterParams(state, requestParams), files)
            : await pageServices.executeComponentAction(addFilterParams(state, requestParams), files);
      } else {
        var { data } =
          actionType === 'procedure'
            ? await pageServices.executeProcedureActionList(requestParams, files)
            : await pageServices.executeComponentActionList(requestParams, files);
      }
      if (data.invalid) {
        showMessage(commit, { message: data.dictionaryReturns['@ds_retorno'] });
        return;
      }
      params.action.after && (await afterEvents(state, commit, dispatch, params, data, requestParams));
    } catch (err) {
      console.error(err);
    } finally {
      eventBus.$emit(HANDLERS.FINISH_LOADING);
    }
  }
};
